/**
 * ValveTimelineChart - Visualización del historial de estados de válvulas
 * 
 * Muestra:
 * - Horas programadas de encendido/apagado
 * - Horas reales de estados de las válvulas
 * - Fuente del cambio de estado (schedule, manual, trigger, system)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Clock,
  Droplets,
  Timer,
  RefreshCw,
  AlertCircle,
  Power,
  PowerOff,
  Activity,
} from 'lucide-react';
import { controllersService, localSchedulesService, schedulesService } from '@/services';
import type { ValveTimeline, ValveStateChange, ApiLocalSchedule, ApiSchedule } from '@/types/api';
import {
  buildLocalWallIso,
  formatMadridDate,
  formatMadridTime,
  getValveEventDisplayIso,
  toTimelineInstant,
} from '@/utils/timezoneDisplay';

interface ValveTimelineChartProps {
  controllerId: string;
  controllerName?: string;
  localMode?: boolean;
  operationMode?: 'local' | 'remote' | 'manual';
}

const sourceLabels: Record<string, { label: string; color: string }> = {
  schedule: { label: 'Programación', color: 'bg-blue-500' },
  manual: { label: 'Manual', color: 'bg-emerald-500' },
  trigger: { label: 'Trigger', color: 'bg-amber-500' },
  system: { label: 'Sistema', color: 'bg-gray-500' },
  scheduled: { label: 'Programado', color: 'bg-purple-500' }, // Para eventos programados
  received: { label: 'Recibido', color: 'bg-green-500' }, // Para eventos recibidos
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatTime(dateStr: string): string {
  return formatMadridTime(dateStr);
}

function formatDate(dateStr: string): string {
  return formatMadridDate(dateStr);
}

/** Itera días YYYY-MM-DD inclusive sin depender del TZ del navegador */
function eachDateInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  let cursor = parseISO(`${startDate}T12:00:00`);
  const end = parseISO(`${endDate}T12:00:00`);
  while (cursor <= end) {
    dates.push(format(cursor, 'yyyy-MM-dd'));
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
  }
  return dates;
}

// Helper: Generar eventos programados desde programaciones locales
function generateScheduledEventsFromLocalSchedules(
  schedules: ApiLocalSchedule[],
  valveChannel: number,
  startDate: string,
  endDate: string
): Array<{ startTime: string; endTime: string; name: string }> {
  const events: Array<{ startTime: string; endTime: string; name: string }> = [];
  const dates = eachDateInRange(startDate, endDate);

  schedules
    .filter(s => s.enabled && (
      (s.valveSelection === 1 && valveChannel === 1) ||
      (s.valveSelection === 2 && valveChannel === 2) ||
      (s.valveSelection === 3)
    ))
    .forEach(schedule => {
      const [startHour, startMin] = schedule.startTime.split(':').map(Number);
      const [endHour, endMin] = schedule.endTime.split(':').map(Number);

      const days = schedule.daysOfWeek.map(day => {
        const dayMap: Record<string, number> = {
          'Lun': 0, 'Mar': 1, 'Mié': 2, 'Jue': 3, 'Vie': 4, 'Sáb': 5, 'Dom': 6
        };
        return dayMap[day] ?? -1;
      }).filter(d => d >= 0);

      dates.forEach(eventDate => {
        const dayOfWeek = (parseISO(`${eventDate}T12:00:00`).getDay() + 6) % 7;
        if (!days.includes(dayOfWeek)) return;

        events.push({
          startTime: buildLocalWallIso(eventDate, startHour, startMin),
          endTime: buildLocalWallIso(eventDate, endHour, endMin),
          name: schedule.name || `Slot ${schedule.slotNumber}`,
        });
      });
    });

  return events.sort((a, b) => a.startTime.localeCompare(b.startTime));
}

// Helper: Generar eventos programados desde programaciones remotas
function generateScheduledEventsFromRemoteSchedules(
  schedules: ApiSchedule[],
  controllerId: string,
  valveChannel: number,
  startDate: string,
  endDate: string,
  controllerValves?: Array<{ id: string; channel: number }>
): Array<{ startTime: string; endTime: string; name: string }> {
  const events: Array<{ startTime: string; endTime: string; name: string }> = [];
  const dates = eachDateInRange(startDate, endDate);

  schedules
    .filter(s => {
      if (!s.enabled || !s.assignments || s.assignments.length === 0) return false;

      return s.assignments.some(a => {
        if (a.controllerId !== controllerId) return false;

        if (controllerValves) {
          const valve = controllerValves.find(v => v.id === a.valveId);
          return valve && valve.channel === valveChannel;
        }

        return false;
      });
    })
    .forEach(schedule => {
      let startHour = 6;
      let startMin = 30;

      if (schedule.startTime.includes('T')) {
        // Horas de programación remota tratadas como reloj España (sin reconversión)
        const timePart = schedule.startTime.split('T')[1] ?? '';
        const [h, m] = timePart.split(':').map(Number);
        if (!Number.isNaN(h) && !Number.isNaN(m)) {
          startHour = h;
          startMin = m;
        }
      } else if (schedule.startTime.includes(':')) {
        [startHour, startMin] = schedule.startTime.split(':').map(Number);
      }

      const duration = schedule.duration || 30;
      const endTotalMin = startHour * 60 + startMin + duration;
      const endHour = Math.floor(endTotalMin / 60) % 24;
      const finalEndMin = endTotalMin % 60;

      dates.forEach(eventDate => {
        const dayOfWeek = parseISO(`${eventDate}T12:00:00`).getDay();
        if (!schedule.daysOfWeek.includes(dayOfWeek)) return;

        events.push({
          startTime: buildLocalWallIso(eventDate, startHour, startMin),
          endTime: buildLocalWallIso(eventDate, endHour, finalEndMin),
          name: schedule.name,
        });
      });
    });

  return events.sort((a, b) => a.startTime.localeCompare(b.startTime));
}

// Timeline bar component - ahora muestra recibidos y programados
function TimelineBar({ 
  stateChanges, 
  scheduledEvents,
  startDate, 
  endDate,
  showScheduled = false,
}: { 
  stateChanges: ValveStateChange[] | null | undefined;
  scheduledEvents?: Array<{ startTime: string; endTime: string; name: string }>;
  startDate: string;
  endDate: string;
  showScheduled?: boolean;
}) {
  // Proteger contra null/undefined
  const safeStateChanges = stateChanges || [];
  const safeScheduledEvents = scheduledEvents || [];
  
  if (safeStateChanges.length === 0 && safeScheduledEvents.length === 0) {
    return (
      <div className="h-8 rounded-md bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">
        Sin actividad en este período
      </div>
    );
  }

  // Create segments from state changes (marco Europe/Madrid / wall-clock)
  const segments: { start: number; end: number; status: 'open' | 'closed'; source?: string }[] = [];
  const rangeStart = toTimelineInstant(startDate);
  const rangeEnd = toTimelineInstant(endDate);
  const totalDuration = Math.max(rangeEnd - rangeStart, 1);

  let lastStatus: 'open' | 'closed' = 'closed';
  let lastTime = rangeStart;

  safeStateChanges.forEach((change) => {
    const changeTime = toTimelineInstant(getValveEventDisplayIso(change));

    if (changeTime > lastTime) {
      segments.push({
        start: ((lastTime - rangeStart) / totalDuration) * 100,
        end: ((changeTime - rangeStart) / totalDuration) * 100,
        status: lastStatus,
      });
    }

    lastStatus = change.newStatus;
    lastTime = changeTime;
  });

  if (lastTime < rangeEnd) {
    segments.push({
      start: ((lastTime - rangeStart) / totalDuration) * 100,
      end: 100,
      status: lastStatus,
    });
  }

  const scheduledSegments: { start: number; end: number }[] = [];
  safeScheduledEvents.forEach(event => {
    const eventStart = toTimelineInstant(event.startTime);
    const eventEnd = toTimelineInstant(event.endTime);

    if (eventStart >= rangeStart && eventStart <= rangeEnd) {
      scheduledSegments.push({
        start: ((eventStart - rangeStart) / totalDuration) * 100,
        end: Math.min(((eventEnd - rangeStart) / totalDuration) * 100, 100),
      });
    }
  });

  return (
    <div className="space-y-2">
      {/* Línea de eventos recibidos */}
      <div className="relative h-8 rounded-md overflow-hidden bg-muted/30 border">
        {segments.map((segment, index) => (
          <div
            key={index}
            className={cn(
              'absolute top-0 bottom-0 transition-colors',
              segment.status === 'open' ? 'bg-success/60' : 'bg-muted/50'
            )}
            style={{
              left: `${segment.start}%`,
              width: `${segment.end - segment.start}%`,
            }}
          />
        ))}
        {/* State change markers */}
        {safeStateChanges.map((change, index) => {
          const displayIso = getValveEventDisplayIso(change);
          const changeTime = toTimelineInstant(displayIso);
          const position = ((changeTime - rangeStart) / totalDuration) * 100;
          const source = sourceLabels[change.source] || sourceLabels.system;
          
          return (
            <Tooltip key={change.id || index}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'absolute top-0 bottom-0 w-0.5 cursor-pointer',
                    source.color
                  )}
                  style={{ left: `${position}%` }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <div className="space-y-1">
                  <p className="font-medium">
                    {change.newStatus === 'open' ? 'Abierta' : 'Cerrada'} (Recibido)
                  </p>
                  <p>{formatTime(displayIso)} - {formatDate(displayIso)}</p>
                  <Badge variant="secondary" className="text-xs">
                    {source.label}
                  </Badge>
                  {change.duration && (
                    <p className="text-muted-foreground">
                      Duración: {formatDuration(change.duration / 60)}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
        <div className="absolute top-0 left-0 px-1 text-[10px] text-muted-foreground bg-background/80 rounded-br">
          Recibido
        </div>
      </div>

      {/* Línea de eventos programados (si hay programaciones) */}
      {showScheduled && safeScheduledEvents.length > 0 && (
        <div className="relative h-8 rounded-md overflow-hidden bg-muted/30 border border-purple-500/30">
          {scheduledSegments.map((segment, index) => {
            const event = safeScheduledEvents[index];
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div
                    className="absolute top-0 bottom-0 bg-purple-500/40 hover:bg-purple-500/60 transition-colors cursor-pointer"
                    style={{
                      left: `${segment.start}%`,
                      width: `${segment.end - segment.start}%`,
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="space-y-1">
                    <p className="font-medium">Programado: {event.name}</p>
                    <p>{formatTime(event.startTime)} - {formatTime(event.endTime)}</p>
                    <p>{formatDate(event.startTime)}</p>
                    <Badge variant="secondary" className="text-xs bg-purple-500/10 text-purple-600">
                      Programado
                    </Badge>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
          <div className="absolute top-0 left-0 px-1 text-[10px] text-muted-foreground bg-background/80 rounded-br">
            Programado
          </div>
        </div>
      )}
    </div>
  );
}

// Valve timeline item
function ValveTimelineItem({ 
  valve, 
  startDate, 
  endDate,
  scheduledEvents,
  showScheduled = false,
}: { 
  valve: ValveTimeline;
  startDate: string;
  endDate: string;
  scheduledEvents?: Array<{ startTime: string; endTime: string; name: string }>;
  showScheduled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Droplets className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">{valve.valveName}</span>
          <Badge variant="secondary" className="text-xs">
            Canal {valve.channel}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Power className="w-3 h-3 text-success" />
            {valve.stats.openCount}
          </span>
          <span className="flex items-center gap-1">
            <Timer className="w-3 h-3" />
            {formatDuration(Math.round(valve.stats.totalOpenTime / 60))}
          </span>
        </div>
      </div>
      <TimelineBar 
        stateChanges={valve.stateChanges || []}
        scheduledEvents={scheduledEvents}
        startDate={startDate}
        endDate={endDate}
        showScheduled={showScheduled}
      />
    </div>
  );
}

// State changes list
function StateChangesList({ stateChanges }: { stateChanges: ValveStateChange[] }) {
  if (stateChanges.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Sin cambios de estado registrados
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {stateChanges.map((change, index) => {
        const source = sourceLabels[change.source] || sourceLabels.system;
        return (
          <div 
            key={change.id || index}
            className={cn(
              'flex items-center justify-between p-2 rounded-md border text-sm',
              change.newStatus === 'open' ? 'bg-success/5 border-success/20' : 'bg-muted/30'
            )}
          >
            <div className="flex items-center gap-2">
              {change.newStatus === 'open' ? (
                <Power className="w-3.5 h-3.5 text-success" />
              ) : (
                <PowerOff className="w-3.5 h-3.5 text-muted-foreground" />
              )}
              <span className={change.newStatus === 'open' ? 'text-success' : 'text-muted-foreground'}>
                {change.newStatus === 'open' ? 'Abierta' : 'Cerrada'}
              </span>
              <Badge variant="outline" className="text-xs h-5">
                {source.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {change.duration && (
                <span>{formatDuration(Math.round(change.duration / 60))}</span>
              )}
              <span>{formatTime(getValveEventDisplayIso(change))}</span>
              <span>{formatDate(getValveEventDisplayIso(change))}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ValveTimelineChart({ 
  controllerId, 
  controllerName,
  localMode = false,
  operationMode = 'remote',
}: ValveTimelineChartProps) {
  const today = new Date();
  const defaultStartDate = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
  const defaultEndDate = format(today, 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [selectedValve, setSelectedValve] = useState<string | null>(null);

  // Query para obtener timeline de válvulas (eventos recibidos)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['valveTimeline', controllerId, startDate, endDate],
    queryFn: async () => {
      const response = await controllersService.getValvesTimeline(controllerId, startDate, endDate);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar historial');
      }
      return response.data;
    },
    enabled: !!controllerId,
  });

  // Query para obtener información de la controladora
  const { data: controllerData } = useQuery({
    queryKey: ['controller', controllerId],
    queryFn: async () => {
      const response = await controllersService.getById(controllerId);
      if (!response.success) {
        return null;
      }
      return response.data;
    },
    enabled: !!controllerId,
  });

  // Determinar si está en modo local
  const isLocalMode = localMode || controllerData?.localMode || false;
  const actualOperationMode = operationMode || controllerData?.operationMode || 'remote';

  // Query para obtener programaciones locales (si está en modo local)
  const { data: localSchedulesData } = useQuery({
    queryKey: ['local-schedules', controllerId],
    queryFn: async () => {
      const response = await localSchedulesService.getByController(controllerId);
      if (!response.success) {
        return null;
      }
      return response.data;
    },
    enabled: !!controllerId && isLocalMode,
  });

  // Query para obtener programaciones remotas (si está en modo remoto)
  const { data: remoteSchedulesData } = useQuery({
    queryKey: ['schedules', controllerId],
    queryFn: async () => {
      const response = await schedulesService.getAll({ 
        controllerId,
        pageSize: 100,
      });
      if (!response.success) {
        return null;
      }
      return response.data;
    },
    enabled: !!controllerId && actualOperationMode === 'remote',
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'Error al cargar historial'}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Proteger contra datos null/undefined
  const valves = data?.valves || [];
  const summary = data?.summary || {
    totalStateChanges: 0,
    totalOpenTime: 0,
    totalClosedTime: 0,
  };

  // Generar eventos programados para cada válvula
  const getScheduledEventsForValve = (valveChannel: number): Array<{ startTime: string; endTime: string; name: string }> => {
    if (isLocalMode && localSchedulesData?.schedules) {
      return generateScheduledEventsFromLocalSchedules(
        localSchedulesData.schedules,
        valveChannel,
        startDate,
        endDate
      );
    } else if (actualOperationMode === 'remote' && remoteSchedulesData?.items) {
      // Obtener las válvulas del controlador para mapear valveId a channel
      const controllerValves = controllerData?.valves?.map(v => ({
        id: v.id,
        channel: v.channel,
      })) || [];
      
      return generateScheduledEventsFromRemoteSchedules(
        remoteSchedulesData.items,
        controllerId,
        valveChannel,
        startDate,
        endDate,
        controllerValves
      );
    }
    return [];
  };

  const selectedValveData = selectedValve 
    ? valves.find(v => v.valveId === selectedValve)
    : null;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">
              Historial de Válvulas
              {controllerName && (
                <span className="text-muted-foreground font-normal ml-2">
                  - {controllerName}
                </span>
              )}
            </CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Date range selector */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="startDate" className="text-xs">Desde</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="endDate" className="text-xs">Hasta</Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <p className="text-2xl font-bold">{summary.totalStateChanges}</p>
            <p className="text-xs text-muted-foreground">Cambios totales</p>
          </div>
          <div className="p-3 rounded-lg bg-success/10 text-center">
            <p className="text-2xl font-bold text-success">
              {formatDuration(Math.round(summary.totalOpenTime / 60))}
            </p>
            <p className="text-xs text-muted-foreground">Tiempo abierto</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <p className="text-2xl font-bold">{valves.length}</p>
            <p className="text-xs text-muted-foreground">Válvulas</p>
          </div>
        </div>

        {/* Valve timelines */}
        {valves.length > 0 ? (
          <div className="space-y-4">
            {valves.map((valve) => {
              const scheduledEvents = getScheduledEventsForValve(valve.channel);
              const hasScheduledEvents = scheduledEvents.length > 0;
              
              return (
                <div 
                  key={valve.valveId}
                  className={cn(
                    'p-3 rounded-lg border cursor-pointer transition-colors',
                    selectedValve === valve.valveId 
                      ? 'border-primary bg-primary/5' 
                      : 'hover:border-muted-foreground/30'
                  )}
                  onClick={() => setSelectedValve(
                    selectedValve === valve.valveId ? null : valve.valveId
                  )}
                >
                  <ValveTimelineItem 
                    valve={valve} 
                    startDate={`${startDate}T00:00:00`}
                    endDate={`${endDate}T23:59:59`}
                    scheduledEvents={scheduledEvents}
                    showScheduled={hasScheduledEvents}
                  />
                  
                  {/* Expanded details */}
                  {selectedValve === valve.valveId && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">Cambios de estado recibidos</p>
                        <StateChangesList stateChanges={valve.stateChanges || []} />
                      </div>
                      {hasScheduledEvents && (
                        <div>
                          <p className="text-sm font-medium mb-2">Eventos programados</p>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {scheduledEvents.map((event, index) => (
                              <div 
                                key={index}
                                className="flex items-center justify-between p-2 rounded-md border text-sm bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800"
                              >
                                <div className="flex items-center gap-2">
                                  <Clock className="w-3.5 h-3.5 text-purple-600" />
                                  <span className="font-medium">{event.name}</span>
                                  <Badge variant="outline" className="text-xs h-5 bg-purple-500/10 text-purple-600">
                                    Programado
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{formatTime(event.startTime)}</span>
                                  <span>-</span>
                                  <span>{formatTime(event.endTime)}</span>
                                  <span>{formatDate(event.startTime)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Droplets className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No hay datos de historial para este período
            </p>
          </div>
        )}

        {/* Legend */}
        <div className="space-y-2 pt-4 border-t">
          <p className="text-xs font-medium text-muted-foreground mb-2">Leyenda - Eventos Recibidos:</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {Object.entries(sourceLabels)
              .filter(([key]) => key !== 'scheduled' && key !== 'received')
              .map(([key, value]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className={cn('w-2 h-2 rounded-full', value.color)} />
                  <span>{value.label}</span>
                </div>
              ))}
          </div>
          {(isLocalMode || actualOperationMode === 'remote') && (
            <>
              <p className="text-xs font-medium text-muted-foreground mt-3 mb-2">Leyenda - Eventos Programados:</p>
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span>Programado</span>
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ValveTimelineChart;
